import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

/**
 * Vertical list loading placeholder. Mirrors the common "icon + two text
 * lines" row used by sidebars, menus and detail lists (POs, reminders,
 * attachments, saved views, …) so the swap to real rows is visually quiet.
 *
 * Prefer this over bare "Loading…" text. See
 * `.cursor/rules/loading-skeletons.mdc`.
 */
interface ListSkeletonProps {
  rows?: number
  /** Render a small leading square (icon/avatar placeholder) per row. */
  leading?: boolean
  /** Wrap the list in a bordered card to match a real `<ul className="border …">`. */
  bordered?: boolean
  className?: string
}

export const ListSkeleton = ({
  rows = 5,
  leading = true,
  bordered = true,
  className,
}: ListSkeletonProps): JSX.Element => {
  return (
    <div
      className={cn(
        bordered && 'rounded-md border bg-card divide-y',
        className,
      )}
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading list"
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-start gap-3 p-3">
          {leading ? <Skeleton className="size-4 shrink-0 rounded" /> : null}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-1/2 rounded" />
            <Skeleton className="h-3 w-3/4 rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}
